import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'

import { Store } from '@ngrx/store'

import {
  FLX_MODAL_DATA,
  FlxIconModule,
  FlxModalModule,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import {
  DefaultActionsColumn,
  DefaultEditedAtColumn,
  DefaultEditedByColumn,
  FlxResourcesGridComponent,
  getActionsColumnLength,
} from '@flowx/designer/modules/projects/components/resources-grid/resources-grid.component'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { VersionChangesStore } from '@flowx/designer/modules/version-changes/version-changes.store'
import { FlxVersionCompareModalComponent } from '@flowx/designer/modules/version-changes/version-compare-modal/version-compare-modal.component'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'

import { FlxCompareVersioningInfoComponent } from './compare-versioning-info/compare-versioning-info.component'
import { ApplicationBranchInfoComponent } from './custom-table-cells/application-branch-info.component'

import { ApplicationVersion, VersionResourceChangeStatus } from '../../entities/application.entity'
import {
  ResourceHistoryParams,
  ResourceHistoryService,
} from '../../services/resource-history.service'
import {
  AvailableResourceZones,
  ResourceNavigationService,
} from '../../services/resource-navigation.service'
import { selectWorkspaceId } from '../../store/selectors/router.selectors'
import { BranchNameComponent } from '../../versioning/branch-name/branch-name.component'
import { ActionsCellComponent } from '../ag-grid/cell-components/actions-cell/actions-cell.component'

@Component({
  selector: 'flx-resource-modal-history',
  imports: [
    FlxModalModule,
    FlxVersionCompareModalComponent,
    FlxIconModule,
    FlxSpinnerModule,
    FlxResourcesGridComponent,
    FlxCompareVersioningInfoComponent,
  ],
  templateUrl: './resource-history-modal.component.html',
  providers: [
    VersionChangesStore,
    ResourceHistoryService,
    BranchNameComponent,
    FlxCompareVersioningInfoComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'w-full h-full',
  },
})
export class FlxResourceHistoryModalComponent {
  #modalService = inject(FlxModalService)
  #data = inject(FLX_MODAL_DATA) as ResourceHistoryParams
  #router = inject(Router)
  #route = inject(ActivatedRoute)
  #versionChangesStore = inject(VersionChangesStore)
  #resourceHistoryService = inject(ResourceHistoryService)
  #applicatioDetailsStore = inject(ApplicationDetailsStore)
  #resourceNavigationService = inject(ResourceNavigationService)
  #store = inject(Store)

  comparedResource = this.#versionChangesStore.comparedResource
  resourceName = this.#data.resourceName
  selectedAppVersion = this.#applicatioDetailsStore.applicationVersion
  selectedWorkspaceId = this.#store.selectSignal(selectWorkspaceId)

  resourceHistory = toSignal(this.#resourceHistoryService.getResourceHistory(this.#data))

  compareWithVersion = computed(() => {
    const resourceHistory = this.resourceHistory()
    const compareWithVersionId = this.#versionChangesStore.compareWithVersionId()
    return resourceHistory?.find((version) => version.id === compareWithVersionId)
  })

  tableColumnsDef = computed(() => {
    const selectedAppVersion = this.selectedAppVersion()
    return [
      {
        headerName: 'Version',
        minWidth: 120,
        field: 'branch',
        cellRenderer: ApplicationBranchInfoComponent,
      },
      {
        ...DefaultEditedByColumn,
        headerName: 'Last edited by',
        field: 'contentModifiedBy',
      },
      {
        ...DefaultEditedAtColumn,
        headerName: 'Last edited at',
        field: 'contentModifiedDate',
      },
      {
        ...DefaultActionsColumn,
        width: getActionsColumnLength(2),
        cellRenderer: ActionsCellComponent<ApplicationVersion>,
        cellRendererParams: (params) => ({
          actions: [
            ...(params.data.id !== selectedAppVersion?.id
              ? [
                  {
                    icon: 'ph-eye',
                    tooltip: 'See changes',
                    tooltipStyleClass: 'text-xs',
                    dataTestId: 'compare-button',
                    clickAction: this.toggleResourceCompare.bind(this),
                  },
                  {
                    icon: 'ph-arrow-square-out',
                    tooltip: 'Open version in new tab',
                    tooltipStyleClass: 'text-xs',
                    dataTestId: 'open-in-new-tab-button',
                    clickAction: this.externalRedirect.bind(this),
                  },
                ]
              : []),
          ],
        }),
      },
    ]
  })

  closeModal(): void {
    this.toggleResourceCompare(null)
    this.#modalService.hide()
  }

  toggleResourceCompare(appVersion: ApplicationVersion | null = null): void {
    const { resourceDefinitionId, resourceType, resourceName } = this.#data
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        comparedResource: appVersion
          ? JSON.stringify({
              resourceChangeStatus: VersionResourceChangeStatus.UPDATED,
              resourceDefinitionId,
              resourceType,
              resourceName,
            })
          : null,
        compareWithVersionId: appVersion ? appVersion.id : null,
      },
      queryParamsHandling: 'merge',
    })
  }

  externalRedirect(
    appVersionWithResourceInfo: ApplicationVersion & { resourceId: string; resourceName: string }
  ): void {
    const { resourceType } = this.#data
    const { id: appVersionId, appId, resourceId, resourceName } = appVersionWithResourceInfo
    const redirectZone = AvailableResourceZones[resourceType]
    this.#resourceNavigationService.redirectToResource(resourceType, {
      appId,
      appVersionId,
      resourceId,
      resourceName,
      workspaceId: this.selectedWorkspaceId(),
      ...(redirectZone && { zone: redirectZone.view }),
    })
  }
}
